import {
  DATA_LAYER_JOURNEY_TYPES,
  DATA_LAYER_SCREEN_TYPES,
  ORDER_TYPES,
  PORT_IN_STATUS,
  PORT_JOURNEY_TYPE,
} from '../constants'
import { PortInDetails } from '@smarty-nx/data'
import { SIM_JOURNEY_TYPES } from '@smarty-nx/code-utils'

export interface SmdlModule {
  id: string | null
  placement?: string
  name?: string
  items: ModuleItem[]
}

export interface SmdlAssets {
  type: string
  products?: {
    category: string
    id: string
    name: string
    value: Record<string, unknown>
    discount?: Record<string, unknown>
  }[]
  promotion?: {
    id: string
    name: string
  }[]
}

export type BaseEvent = {
  event: string
}

export type SmdlScreenEvent = BaseEvent & {
  screen: { name: string; type: string }
}

export type SmdlModuleEvent = BaseEvent & {
  assets?: SmdlAssets
  modules: SmdlModule[]
}

export type SmdlModuleClickEvent = BaseEvent & {
  moduleItem: {
    id: string
    type: string
    moduleId: string
  }
}

export type SmdlCheckoutEvent = BaseEvent & {
  checkoutType: string
  product: {
    id: string
  }
}

export type SmdlProductViewEvent = BaseEvent & {
  product: {
    id: string
  }
}

export type SmdlEvent =
  | SmdlScreenEvent
  | SmdlModuleEvent
  | SmdlProductViewEvent
  | SmdlModuleClickEvent
  | SmdlCheckoutEvent
  | (BaseEvent & Record<string, unknown>) // general fallback

export interface SmdlScreen {
  name?: string
  namePrefix?: string
  type: DATA_LAYER_SCREEN_TYPES
  lead_generator?: string
  order_type?: string
  port_journey_type?: PORT_JOURNEY_TYPE
  sim_type?: string
  accountType?: string
  port_in?: { details: PortInDetails | null }
}

export interface SmdlPlatform {
  version?: string
  commit?: string
  sessionId?: string
}

export interface SmdlGroup {
  id: number | null
  isGroupMember?: string
  invitations: number
  groupSize: number
  groupAccountType?: string
  isOverSixteen?: string
}

export interface SmdlGroupInfo {
  group: SmdlGroup
  inviteType: string | null
}

export type SmdlUser =
  | {
      status: 'logged-in'
      customer: {
        id: string
        plan: string
        actingAs?: string
        order_type?: string
      }
    }
  | {
      status: 'logged-out'
    }

export interface Smdl {
  events: DataLayerEvent[]
  screen?: SmdlScreen
  platform: SmdlPlatform
  user?: SmdlUser
  cart?: CartObject | null
  product?: ProductObject | AddonProductObject | null
  groupInfo?: SmdlGroupInfo
}

export interface DataLayerEvent {
  event: string
  cart?: CartObject | null
  form?: FormObject
  modules?: SmdlModule[]
  // TODO Understand with DMPG why there are multiple ways of tracking module events
  module?: Module
  moduleItem?: ModuleItem
  checkoutType?: string
  assets?: SmdlAssets
  product?: ProductObject | AddonProductObject | null
  products?: (ProductObject | AddonProductObject | undefined)[]
  screen?: SmdlScreen
  user?: UserObject | null
  transaction?: TransactionData | AddonTransactionData
  search?: CountrySearchObject
  tools?: Tools
  groupInfo?: SmdlGroupInfo | null
  error?: string[]
  errors?: string[] | Record<string, string>
  port_journey_type?: string
  order_type?: ORDER_TYPES | SIM_JOURNEY_TYPES

  [other: string | number | symbol]: unknown
}

export interface CartObject {
  checkoutStep?: number
  checkoutType?: DATA_LAYER_JOURNEY_TYPES
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  lines: any
  value?: ValueObject | AddonValueObject
}

export interface CartV2Object {
  checkoutType?: DATA_LAYER_JOURNEY_TYPES
  order_type?: ORDER_TYPES
  orderType?: string
  leadGenerator?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  lines?: any[]
  value?: ValueObject
  number_of_sims?: number
}

export interface FormEventProps {
  eventName: string
  formData: FormObject
  userObject?: UserObject | null
  error?: Array<string> | string | null
  marketingPref?: string
}

export interface FormObject {
  id: string | null
  name?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fields?: Array<any>
  error?: Array<string> | string | null
}

export interface Module {
  action: string
}

export interface ModuleItem {
  id?: string
  position?: string | number | null
  type: string | null
  moduleId?: string | null
}

export interface ProductObject {
  id: string
  name?: string
  category?: string
  value?: ValueObject
  currency?: string
  quantity?: number
  discount?: DiscountData
  order_type?: string
}

export interface AddonProductObject {
  id: string
  name?: string
  category?: string
  value?: AddonValueObject
}

export interface Tools {
  name: string
  status: string
}

export interface ValueObject {
  chargeType?: string
  displayGross?: number
  displayTax?: number
  discount?: DiscountData
}

export interface AddonValueObject {
  chargeType?: string
  displayGross: number
  displayTax: number
}

export interface UserObject {
  status?: string
  customer?: {
    id?: string
    plan?: string
    plan_status?: string
    offer_name?: string
    marketing_preferences?: string
    // TODO: this one under discussion
    group_size?: number
  }
  porting?: {
    status?: string
    option?: string
    groupMember?:
      | {
          status: string
          option: string
        }
      | {
          status: string
          option: string
        }[]
  }
}

export interface PaymentData {
  type?: string | null
  value: ValueObject
}

export interface DiscountData {
  coupon: string | null
  value: ValueObject
}

export interface LinesData {
  id: string
  name: string
  category: string
  quantity: number
  value: ValueObject
}

export interface TransactionData {
  id?: string
  'lead-generator'?: string | null
  lead_generator?: string | null
  order_type?: string | null
  payment?: PaymentData[]
  cart?: CartV2Object
  selected_port_in_option?: PORT_IN_STATUS
}

export interface AddonTransactionData {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  transaction: any
  user: UserObject | null
}

export interface CountrySearchObject {
  keyword: string
  type_of_use: string
}

export interface DataLayerObject {
  asset?: AssetData
  type?: string
  discount?: DiscountData
  product?: ProductObject | AddonProductObject | undefined
  cart?: Record<string, unknown>
  lines?: LinesData
  transaction?: { id: string }
  event?: Record<string, unknown>
  module?: Record<string, unknown>
  orderType?: string
  selectedPortInOption?: PORT_IN_STATUS
  leadGenerator?: string
}

export interface AssetData {
  category: string
  name: string
  value: ValueObject
  discount: {
    value: ValueObject
  }
}

export type AssetItem = {
  type: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}

export enum PortInOption {
  PAC_WITH_DATE = 'port-in-pac-with-date',
  PAC_WITHOUT_DATE = 'port-in-pac-no-date',
  STAC_WITH_DATE = 'port-in-stac-with-date',
  STAC_WITHOUT_DATE = 'port-in-stac-no-date',
  PORT_IN_LATER = 'port-in-later',
}
